import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationService } from '../../navigation.service';

@Component({
    selector: 'gtw-sub-menus',
    templateUrl: './sub-menus.component.html',
    styleUrls: ['./sub-menus.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SubMenusComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() showScreensEvent = new EventEmitter<any>();
  @ViewChildren('accordionGroup') accordionGroups!: QueryList<any>;
  @ViewChildren('panelRoot') panelRoots!: QueryList<ElementRef<HTMLElement>>;

  @Input('selected_sub_task')
  set _taskskList(val: any) {
    this.tasksList = val;
    if (this.isAccordionOpen && this.tasksList) {
      this.applyBulkState(true);
    }
  }
  tasksList: any;

  @Input('pannel-position')
  set _pannelPosition(val: number) {
    this.pannelPosition = val;
  }

  isAccordionOpen: boolean = false;
  private isBulkToggling = false;
  private panelListenHandles: Array<() => void> = [];
  private panelRootsSub?: Subscription;
  private pendingToggle: { accordion: HTMLElement; wasExpanded: boolean } | null = null;

  @Input('is-accordion-open')
  set _isAccordionOpen(value: boolean) {
    this.isAccordionOpen = value;
    this.applyBulkState(value);
  }

  mSetting: any = { showScreen: true };
  @Input('m-setting')
  set _mSetting(value: any) {
    this.mSetting = value;
  }

  pannelPosition = 0;

  constructor(private navigationService: NavigationService) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.bindPanelRoots();
    this.panelRootsSub = this.panelRoots?.changes.subscribe(() => this.bindPanelRoots());
    if (this.isAccordionOpen) {
      this.applyBulkState(true);
    }
  }

  ngOnDestroy(): void {
    this.panelRootsSub?.unsubscribe();
    this.clearPanelListeners();
  }

  showScreens(screen: any) {
    this.showScreensEvent.emit(screen);
  }

  onAccordionClick(task: any): void {
    if (this.isBulkToggling) return;
    if (task.screens && !this.mSetting.showScreen) {
      task.isClicked = true;
      if (task.screens[0]) {
        task.screens[0].isActive = true;
        this.showScreens(task.screens[0]);
      }
    }
  }

  getAccordionStyleClass(task: any): string {
    if (task.screens && !this.mSetting.showScreen && task.isClicked) {
      return 'screen-no-show-clicked';
    }
    if (task.screens && !this.mSetting.showScreen && !task.isClicked) {
      return 'screen-no-show';
    }
    return '';
  }

  getTaskAccordionKey(task: any, index: number): string {
    return `${task?.id ?? `idx-${index}`}`;
  }

  trackByTaskId(index: number, item: any): any {
    return item?.id ?? index;
  }

  private applyBulkState(open: boolean): void {
    this.isBulkToggling = true;
    setTimeout(() => {
      const components = this.accordionComponents();
      if (components.some((el: any) => !!el?.content)) {
        this.accordionGroups?.forEach((group: any) => {
          if (open) {
            group.accordionList?.forEach((el: any) => {
              if (!el.content || el.expanded) return;
              group.openAccordion(el);
            });
          } else {
            group.collapseAll?.();
          }
        });
      } else {
        this.accordionElements().forEach((root) => this.setAccordionOpen(root, open));
      }
      this.isBulkToggling = false;
    }, 0);
  }

  private bindPanelRoots(): void {
    this.clearPanelListeners();
    this.panelRoots?.forEach((ref) => {
      const el = ref.nativeElement;
      const capture = (event: Event) => this.onNavToggleCapture(event as MouseEvent);
      const bubble = (event: Event) => this.onNavToggleBubble(event as MouseEvent);
      el.addEventListener('click', capture, true);
      el.addEventListener('click', bubble);
      this.panelListenHandles.push(() => {
        el.removeEventListener('click', capture, true);
        el.removeEventListener('click', bubble);
      });
    });
  }

  private clearPanelListeners(): void {
    this.panelListenHandles.forEach((stop) => stop());
    this.panelListenHandles = [];
  }

  private onNavToggleCapture(event: MouseEvent): void {
    const accordion = this.accordionFromEvent(event);
    if (!accordion) {
      this.pendingToggle = null;
      return;
    }
    this.pendingToggle = {
      accordion,
      wasExpanded: accordion.classList.contains('expanded')
    };
  }

  private onNavToggleBubble(event: MouseEvent): void {
    const pending = this.pendingToggle;
    this.pendingToggle = null;
    if (!pending || this.isBulkToggling || !this.accordionFromEvent(event)) {
      return;
    }
    if (pending.accordion.classList.contains('expanded') !== pending.wasExpanded) {
      return;
    }
    const open = !pending.wasExpanded;
    this.setAccordionOpen(pending.accordion, open);
    const task = this.taskFromAccordion(pending.accordion);
    if (task) {
      this.onAccordionClick(task);
    }
  }

  private accordionFromEvent(event: MouseEvent): HTMLElement | null {
    const target = event.target as HTMLElement | null;
    const toggle = target?.closest?.('.ap-accordion-toggle') as HTMLElement | null;
    return toggle?.parentElement ?? null;
  }

  private setAccordionOpen(root: HTMLElement, open: boolean): void {
    const component = this.accordionComponents().find((el: any) => el?.id && el.id === root.id);
    if (component) {
      component.expanded = open;
    }
    root.classList.toggle('expanded', open);
    const content = root.querySelector(':scope > .ap-accordion-content') as HTMLElement | null;
    const icon = root.querySelector(':scope > .ap-accordion-toggle .icon-down-chevron-outline');
    if (content) {
      content.classList.toggle('hidden', !open);
      content.style.display = open ? '' : 'none';
      content.style.height = '';
      content.style.overflow = '';
      content.style.removeProperty('padding-top');
      content.style.removeProperty('padding-bottom');
      content.style.removeProperty('margin-top');
      content.style.removeProperty('margin-bottom');
      content.style.removeProperty('transition');
    }
    icon?.classList.toggle('rotate', open);
  }

  private accordionElements(): HTMLElement[] {
    const roots: HTMLElement[] = [];
    this.panelRoots?.forEach((ref) => {
      ref.nativeElement.querySelectorAll('.ap-accordion-toggle').forEach((node) => {
        const root = (node as HTMLElement).parentElement;
        if (root) {
          roots.push(root);
        }
      });
    });
    return roots;
  }

  private accordionComponents(): any[] {
    const components: any[] = [];
    this.accordionGroups?.forEach((group: any) => {
      group.accordionList?.forEach((el: any) => components.push(el));
    });
    return components;
  }

  private taskFromAccordion(root: HTMLElement): any {
    const id = root.id || '';
    if (id.startsWith('accordion-')) {
      return this.findTask(this.tasksList, id.slice('accordion-'.length));
    }
    if (id.startsWith('category-')) {
      return (this.tasksList || []).find((category: any) => String(category?.id) === id.slice('category-'.length));
    }
    return null;
  }

  private findTask(list: any[], id: string): any {
    if (!list?.length) {
      return null;
    }
    for (const task of list) {
      if (String(task?.id) === id) {
        return task;
      }
      const nested = this.findTask(task?.children, id);
      if (nested) {
        return nested;
      }
    }
    return null;
  }
}
